import React, { createContext, useContext, useState, useEffect } from 'react';
import translationDA from './locales/da.json';
import translationDE from './locales/de.json';

type Language = 'da' | 'de';

interface Translations {
  [key: string]: any;
}

const translations: Record<Language, Translations> = {
  da: translationDA,
  de: translationDE
};

const getInitialLanguage = (): Language => {
  const savedLang = localStorage.getItem('appLanguage');
  if (savedLang === 'da' || savedLang === 'de') {
    return savedLang;
  }

  const browserLang = navigator.language || (navigator.languages && navigator.languages[0]);
  if (browserLang) {
    const langCode = browserLang.toLowerCase().split('-')[0];
    if (langCode === 'de') {
      localStorage.setItem('appLanguage', 'de');
      return 'de';
    } else if (langCode === 'da') {
      localStorage.setItem('appLanguage', 'da');
      return 'da';
    }
  }

  return 'da';
};

const getTranslation = (translations: Translations, key: string): string => {
  const keys = key.split('.');
  let result: any = translations;
  
  for (const k of keys) {
    if (result && typeof result === 'object' && k in result) {
      result = result[k];
    } else {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
  }
  
  return typeof result === 'string' ? result : key;
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage());

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('appLanguage', lang);
  };

  const t = (key: string): string => {
    return getTranslation(translations[language], key);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export interface WithLanguage {
  t: (key: string) => string;
  language: Language;
  setLanguage: (lang: Language) => void;
}

export function withLanguage<P extends WithLanguage>(
  Component: React.ComponentType<P>
): React.FC<Omit<P, keyof WithLanguage>> {
  return (props: Omit<P, keyof WithLanguage>) => {
    const languageContext = useLanguage();
    return <Component {...(props as P)} {...languageContext} />;
  };
}
